import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { DuplicateToWorkspaceModal } from "./DuplicateToWorkspaceModal";

import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { useDuplicateEvaluation, useDuplicatePromptTemplateToWorkspace } from "@/queries";
import { Entity, EntityType, getRequiredEntityNumericId } from "@/queries/folder-entities/types";
import { useDuplicateToolRegistry } from "@/queries/tool-registry";
import { useDuplicateWorkflow } from "@/queries/workflows";
import { displayToast } from "@/utils/toast";

interface DuplicateEntityDialogProps {
  open: boolean;
  entity: Entity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface DuplicateConfig {
  objectType: string;
  navigationPath: (workspaceId: number, entityId: number) => string;
  successMessage: string;
}

export const DuplicateEntityDialog: React.FC<DuplicateEntityDialogProps> = ({
  open,
  entity,
  onClose,
  onSuccess,
}) => {
  const authContext = useAuth();
  const { userToken } = authContext!;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspaceId: currentWorkspaceId, folderId } = useParams();

  const duplicatePromptMutation = useDuplicatePromptTemplateToWorkspace(userToken!);
  const duplicateWorkflowMutation = useDuplicateWorkflow();
  const duplicateEvaluationMutation = useDuplicateEvaluation();
  const duplicateToolMutation = useDuplicateToolRegistry();

  const duplicateConfig: Record<EntityType, DuplicateConfig | null> = useMemo(
    () => ({
      [EntityType.PROMPT]: {
        objectType: "Prompt Template",
        navigationPath: (workspaceId, entityId) => `/workspace/${workspaceId}/prompt/${entityId}`,
        successMessage: "Prompt template duplicated successfully",
      },
      [EntityType.WORKFLOW]: {
        objectType: "Workflow",
        navigationPath: (workspaceId, entityId) => `/workspace/${workspaceId}/workflow/${entityId}`,
        successMessage: "Workflow duplicated successfully",
      },
      [EntityType.REPORT]: {
        objectType: "Evaluation",
        navigationPath: (workspaceId, entityId) =>
          `/workspace/${workspaceId}/evaluate/blueprints/${entityId}`,
        successMessage: "Evaluation duplicated successfully",
      },
      [EntityType.SNIPPET]: {
        objectType: "Snippet",
        navigationPath: (workspaceId, entityId) => `/workspace/${workspaceId}/prompt/${entityId}`,
        successMessage: "Snippet duplicated successfully",
      },

      [EntityType.FOLDER]: null,
      [EntityType.DATASET]: null,
      [EntityType.AB_TEST]: null,
      [EntityType.INPUT_VARIABLE_SET]: null,
      [EntityType.SMART_TABLE]: null,
      [EntityType.SKILL_COLLECTION]: null,
      [EntityType.TOOL]: {
        objectType: "Tool",
        navigationPath: (workspaceId, entityId) => `/workspace/${workspaceId}/tool/${entityId}`,
        successMessage: "Tool duplicated successfully",
      },
    }),
    [],
  );

  const config = entity ? duplicateConfig[entity.type] : null;

  const isLoading = useMemo(() => {
    switch (entity?.type) {
      case EntityType.PROMPT:
      case EntityType.SNIPPET:
        return duplicatePromptMutation.isPending;
      case EntityType.WORKFLOW:
        return duplicateWorkflowMutation.isPending;
      case EntityType.REPORT:
        return duplicateEvaluationMutation.isPending;
      case EntityType.TOOL:
        return duplicateToolMutation.isPending;
      default:
        return false;
    }
  }, [
    entity?.type,
    duplicatePromptMutation.isPending,
    duplicateWorkflowMutation.isPending,
    duplicateEvaluationMutation.isPending,
    duplicateToolMutation.isPending,
  ]);

  const handleDuplicate = useCallback(
    async (workspaceId: number) => {
      if (!entity || !config) {
        return;
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let result: any;

        // Only pass folder_id when duplicating to the same workspace
        const currentFolderId = folderId ? Number(folderId) : undefined;

        switch (entity.type) {
          case EntityType.PROMPT:
          case EntityType.SNIPPET:
            result = await duplicatePromptMutation.mutateAsync({
              prompt_template_id: entity.id.toString(),
              workspace_id: workspaceId,
              folder_id: currentFolderId,
            });

            if (result.success && result.prompt_template) {
              // Invalidate folder view query
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[2] === (folderId ? Number(folderId) : undefined),
              });

              // Invalidate sidebar query (flatten: true, folder_id: null)
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[5] === true, // flatten = true
              });

              displayToast(config.successMessage, ToastType.success);

              navigate(config.navigationPath(workspaceId, result.prompt_template.id));
            } else {
              throw new Error(result.error || "Failed to duplicate prompt template");
            }
            break;

          case EntityType.WORKFLOW:
            result = await duplicateWorkflowMutation.mutateAsync({
              workflowId: getRequiredEntityNumericId(entity.id),
              workspaceId,
              folderId: currentFolderId,
            });

            if (result.success && result.workflow) {
              // Invalidate folder view query
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[2] === (folderId ? Number(folderId) : undefined),
              });

              // Invalidate sidebar query (flatten: true, folder_id: null)
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[5] === true, // flatten = true
              });

              displayToast(config.successMessage, ToastType.success);
              navigate(config.navigationPath(result.workflow.workspace_id, result.workflow.id));
            } else {
              throw new Error(result.error || "Failed to duplicate workflow");
            }
            break;

          case EntityType.REPORT:
            result = await duplicateEvaluationMutation.mutateAsync({
              evaluationId: getRequiredEntityNumericId(entity.id),
              selectedWorkspaceId: workspaceId,
              folderId: currentFolderId,
            });

            if (result.success && result.evaluation) {
              // Invalidate folder view query
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[2] === (folderId ? Number(folderId) : undefined),
              });

              // Invalidate sidebar query (flatten: true, folder_id: null)
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[5] === true, // flatten = true
              });

              displayToast(config.successMessage, ToastType.success);
              navigate(config.navigationPath(result.evaluation.workspace_id, result.evaluation.id));
            } else {
              throw new Error(result.error || "Failed to duplicate evaluation");
            }
            break;

          case EntityType.TOOL:
            result = await duplicateToolMutation.mutateAsync({
              workspaceId: Number(currentWorkspaceId),
              toolId: getRequiredEntityNumericId(entity.id),
              targetWorkspaceId: workspaceId,
              folderId: currentFolderId,
            });

            if (result.success && result.tool_registry) {
              // Invalidate folder view query
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[2] === (folderId ? Number(folderId) : undefined),
              });

              // Invalidate sidebar query (flatten: true, folder_id: null)
              queryClient.invalidateQueries({
                predicate: query =>
                  query.queryKey[0] === "folder-entities" &&
                  query.queryKey[1] === workspaceId.toString() &&
                  query.queryKey[5] === true, // flatten = true
              });

              displayToast(config.successMessage, ToastType.success);
              navigate(config.navigationPath(workspaceId, result.tool_registry.id));
            } else {
              throw new Error("Failed to duplicate tool");
            }
            break;

          default:
            throw new Error(`Duplication not supported for ${entity.type}`);
        }

        onClose();
        onSuccess?.();
      } catch {
        // eslint-disable-next-line no-console
        console.error("Error handling duplicate");
      }
    },
    [
      entity,
      config,
      duplicatePromptMutation,
      duplicateWorkflowMutation,
      duplicateEvaluationMutation,
      duplicateToolMutation,
      navigate,
      onClose,
      onSuccess,
      queryClient,
      currentWorkspaceId,
      folderId,
    ],
  );

  return (
    <DuplicateToWorkspaceModal
      open={open}
      object={entity}
      objectType={config?.objectType}
      objectTitle={entity?.name}
      setOpen={onClose}
      onDuplicate={handleDuplicate}
      isLoading={isLoading}
    />
  );
};

export default DuplicateEntityDialog;
